"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import {
  PublicJobAlertSubscriptionRequestSchema,
  type PublicJobAlertSubscriptionResponseDto,
} from "@artha/api-contracts";
import {
  type JobAlertFrequency,
  type JobAlertExperience,
  type JobAlertChannel,
  type PublicJobAlertStorage,
  type PublicJobAlertFeedback,
  type PublicJobAlertSubscriptionController as SharedPublicJobAlertSubscriptionController,
  JOB_ALERT_FREQUENCIES,
  JOB_ALERT_EXPERIENCES,
  JOB_ALERT_CHANNELS,
} from "@artha/types";
import {
  isValidWhatsAppNumber,
  normalizeWhatsAppNumber,
} from "@artha/utils";

export const DEFAULT_JOB_ALERT_FREQUENCY: JobAlertFrequency = "weekly";
export const DEFAULT_JOB_ALERT_EXPERIENCE: JobAlertExperience = "fresher";
export const DEFAULT_JOB_ALERT_CHANNEL: JobAlertChannel = "email";
import {
  normalizePublicJobAlertUsername,
  savePublicJobAlertSubscription,
} from "../services/jobAlertClient";
import {
  readCandidateIdentity,
  writeCandidateIdentity,
} from "../../candidate-personalization/storage";

export const PUBLIC_JOB_ALERT_DRAFT_STORAGE_KEY_PREFIX =
  "public-job-alert-subscription-draft";

export const getPublicJobAlertDraftStorageKey = (username: string) =>
  `${PUBLIC_JOB_ALERT_DRAFT_STORAGE_KEY_PREFIX}:${normalizePublicJobAlertUsername(username)}`;

export type PublicJobAlertSubscriptionController =
  SharedPublicJobAlertSubscriptionController<PublicJobAlertSubscriptionResponseDto>;

const DEFAULT_STORAGE: PublicJobAlertStorage = {
  externalId: null,
  channel: DEFAULT_JOB_ALERT_CHANNEL,
  frequency: DEFAULT_JOB_ALERT_FREQUENCY,
  experience: DEFAULT_JOB_ALERT_EXPERIENCE,
};

const getBrowserTimezone = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof timezone === "string" && timezone.trim()
      ? timezone
      : undefined;
  } catch {
    return undefined;
  }
};

const readStorage = (username: string): PublicJobAlertStorage => {
  if (typeof window === "undefined") {
    return DEFAULT_STORAGE;
  }

  try {
    const raw = window.localStorage.getItem(
      getPublicJobAlertDraftStorageKey(username),
    );
    if (!raw) return DEFAULT_STORAGE;

    const parsed = JSON.parse(raw) as Partial<PublicJobAlertStorage>;
    const externalId =
      typeof parsed.externalId === "string" ? parsed.externalId : null;
    const channel =
      parsed.channel &&
      (JOB_ALERT_CHANNELS as readonly string[]).includes(parsed.channel)
        ? (parsed.channel as JobAlertChannel)
        : DEFAULT_STORAGE.channel;
    const frequency =
      parsed.frequency &&
      (JOB_ALERT_FREQUENCIES as readonly string[]).includes(parsed.frequency)
        ? (parsed.frequency as JobAlertFrequency)
        : DEFAULT_STORAGE.frequency;
    const experience =
      parsed.experience &&
      (JOB_ALERT_EXPERIENCES as readonly string[]).includes(parsed.experience)
        ? (parsed.experience as JobAlertExperience)
        : DEFAULT_STORAGE.experience;

    return {
      externalId,
      channel,
      frequency,
      experience,
    };
  } catch {
    return DEFAULT_STORAGE;
  }
};

export function usePublicJobAlertSubscription(
  username: string,
  candidateEmail?: string | null,
): PublicJobAlertSubscriptionController {
  const t = useTranslations("profile");
  const posthog = usePostHog();
  const normalizedUsername = useMemo(
    () => normalizePublicJobAlertUsername(username),
    [username],
  );

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [channel, setChannel] = useState<JobAlertChannel>(() => {
    return readStorage(normalizedUsername).channel || DEFAULT_JOB_ALERT_CHANNEL;
  });
  const [subscription, setSubscription] = useState<PublicJobAlertStorage>(() =>
    readStorage(normalizedUsername),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeModalCardId, setActiveModalCardId] = useState<string | null>(
    null,
  );
  const [feedback, setFeedback] = useState<PublicJobAlertFeedback>({
    cardId: null,
    errorMessage: null,
    successMessage: null,
  });

  useEffect(() => {
    const stored = readStorage(normalizedUsername);
    setSubscription(stored);
    if (stored.channel) {
      setChannel(stored.channel);
    }
  }, [normalizedUsername]);

  useEffect(() => {
    const normalizedCandidateEmail = candidateEmail?.trim();
    if (!normalizedCandidateEmail) return;
    setEmail((current) =>
      current.trim() ? current : normalizedCandidateEmail,
    );
  }, [candidateEmail]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        getPublicJobAlertDraftStorageKey(normalizedUsername),
        JSON.stringify({ ...subscription, channel }),
      );
    } catch {
      // Ignore localStorage sync failures.
    }
  }, [channel, normalizedUsername, subscription]);

  const setFrequency = useCallback((value: JobAlertFrequency) => {
    setSubscription((current) => ({
      ...current,
      frequency: value,
    }));
  }, []);

  const setExperience = useCallback((value: JobAlertExperience) => {
    setSubscription((current) => ({
      ...current,
      experience: value,
    }));
  }, []);

  const submit = useCallback(
    async (cardId: string) => {
      const isWhatsApp = channel === "whatsapp";
      const payload = {
        email: isWhatsApp ? undefined : email.trim() || undefined,
        phone: isWhatsApp ? normalizeWhatsAppNumber(phone) : undefined,
        channel,
        frequency: subscription.frequency,
        experience: subscription.experience,
        timezone: getBrowserTimezone(),
        externalId: readCandidateIdentity().externalId || undefined,
        posthogDistinctId: posthog?.get_distinct_id?.(),
      };

      if (isWhatsApp && (!payload.phone || !isValidWhatsAppNumber(payload.phone))) {
        setFeedback({
          cardId,
          errorMessage: t("jobAlertInvalidPhone"),
          successMessage: null,
        });
        return null;
      }

      const parsedPayload =
        PublicJobAlertSubscriptionRequestSchema.safeParse(payload);

      if (!parsedPayload.success) {
        setFeedback({
          cardId,
          errorMessage: isWhatsApp
            ? t("jobAlertInvalidPhone")
            : t("jobAlertInvalidEmail"),
          successMessage: null,
        });
        return null;
      }

      setIsSubmitting(true);
      setFeedback({
        cardId,
        errorMessage: null,
        successMessage: null,
      });

      try {
        const response = await savePublicJobAlertSubscription(
          normalizedUsername,
          parsedPayload.data,
        );

        setSubscription((current) => ({
          externalId: response.externalId,
          channel: response.channel ?? current.channel,
          frequency: response.frequency ?? current.frequency,
          experience: response.experience ?? current.experience,
        }));
        const currentIdentity = readCandidateIdentity();
        const candidateExternalId =
          response.candidateExternalId || currentIdentity.externalId;
        writeCandidateIdentity({
          externalId: candidateExternalId,
          resumePersonalized: currentIdentity.resumePersonalized,
          candidatePreferences: currentIdentity.candidatePreferences,
        });
        setFeedback({
          cardId,
          errorMessage: null,
          successMessage: t("jobAlertSuccess"),
        });

        return response;
      } catch (error) {
        setFeedback({
          cardId,
          errorMessage:
            error instanceof Error && error.message
              ? error.message
              : t("jobAlertError"),
          successMessage: null,
        });
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      channel,
      email,
      normalizedUsername,
      phone,
      posthog,
      subscription.experience,
      subscription.frequency,
      t,
    ],
  );

  return {
    email,
    phone,
    channel,
    frequency: subscription.frequency,
    experience: subscription.experience,
    externalId: subscription.externalId,
    isSubmitting,
    feedback,
    setEmail,
    setPhone,
    setChannel,
    setFrequency,
    setExperience,
    isModalOpen: activeModalCardId !== null,
    activeModalCardId,
    setActiveModalCardId,
    submit,
  };
}
